const STORAGE_KEY = 'hydraWishlist.v1';
const CHANGE_EVENT = 'hydra:wishlist:change';
const WISHLIST_MAX_QUANTITY = 6;
const WISHLIST_ALREADY_IN_CART_ERROR = 'The maximum quantity of this item is already in your cart.';
const WISHLIST_ADD_ERROR = 'Unable to add item to cart';
const productCache = new Map();
const variantDetailsCache = new Map();
let toastTimeout;
let toastTransitionTimeout;
let queuedToast;
const WISHLIST_ICON = `
  <svg viewBox="0 0 24 24" fill="none" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path class="wishlist-icon__fill" d="M12 20.35 10.55 19.03C5.4 14.36 2 11.27 2 7.5 2 4.41 4.42 2 7.5 2c1.74 0 3.41.81 4.5 2.08C13.09 2.81 14.76 2 16.5 2 19.58 2 22 4.41 22 7.5c0 3.77-3.4 6.86-8.55 11.54L12 20.35Z" fill="currentColor" opacity="0"/>
    <path d="M12 20.35 10.55 19.03C5.4 14.36 2 11.27 2 7.5 2 4.41 4.42 2 7.5 2c1.74 0 3.41.81 4.5 2.08C13.09 2.81 14.76 2 16.5 2 19.58 2 22 4.41 22 7.5c0 3.77-3.4 6.86-8.55 11.54L12 20.35Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
  </svg>
`;

const getWishlist = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
  } catch {
    return [];
  }
};

const setWishlist = (handles) => {
  const uniqueHandles = [...new Set(handles)];

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueHandles));
  } catch {
    return;
  }

  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles: uniqueHandles } }));
};

const hasProduct = (handle) => getWishlist().includes(handle);
const addProduct = (handle) => setWishlist([...getWishlist(), handle]);
const removeProduct = (handle) => setWishlist(getWishlist().filter((item) => item !== handle));
const toggleProduct = (handle) => (hasProduct(handle) ? removeProduct(handle) : addProduct(handle));

const ensureWishlistToast = () => {
  let toast = document.querySelector('[data-wishlist-toast]');
  if (toast) return toast;

  const style = document.createElement('style');
  style.textContent = `
    .wishlist-toast {
      position: fixed;
      right: 50%;
      bottom: max(22px, env(safe-area-inset-bottom));
      z-index: 2147483000;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 42px;
      max-width: calc(100vw - 32px);
      gap: 14px;
      padding: 0 18px 0 28px;
      border-radius: 4px;
      background: #e8f1fe;
      color: rgb(9 86 141);
      box-shadow: 0 12px 32px rgb(0 0 0 / 18%);
      font-family: 'Open Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      white-space: nowrap;
      pointer-events: auto;
      opacity: 0;
      transform: translate(50%, 14px) scale(0.98);
      transition:
        opacity 0.7s ease,
        transform 0.18s ease;
    }

    .wishlist-toast.is-visible {
      opacity: 1;
      transform: translate(50%, 0) scale(1);
    }

    .wishlist-toast.is-hiding {
      transform: translate(50%, 0) scale(1);
    }

    .wishlist-toast__message {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .wishlist-toast__view {
      color: #000000;
      text-decoration: underline;
      text-underline-offset: 3px;
      white-space: nowrap;
    }

    .wishlist-toast__view[hidden] {
      display: none;
    }

    .wishlist-toast__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      min-width: 28px;
      height: 28px;
      min-height: 28px;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;
      font: inherit;
      font-size: 18px;
      line-height: 1;
    }

    .wishlist-toast__close:hover,
    .wishlist-toast__close:focus-visible {
      background: rgb(9 86 141 / 8%);
      outline: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .wishlist-toast {
        transition: none;
      }
    }
  `;

  toast = document.createElement('div');
  toast.className = 'wishlist-toast';
  toast.setAttribute('data-wishlist-toast', '');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <span class="wishlist-toast__message" data-wishlist-toast-message></span>
    <a href="/pages/wishlist" class="wishlist-toast__view" data-wishlist-toast-view hidden>View</a>
    <button type="button" class="wishlist-toast__close" data-wishlist-toast-close aria-label="Dismiss notification">&times;</button>
  `;

  toast.querySelector('[data-wishlist-toast-close]')?.addEventListener('click', () => {
    window.clearTimeout(toastTimeout);
    window.clearTimeout(toastTransitionTimeout);
    queuedToast = null;
    toast.style.transitionDuration = '';
    toast.classList.add('is-hiding');
    toast.classList.remove('is-visible');
  });

  document.head.append(style);
  document.body.append(toast);
  return toast;
};

const setWishlistToastContent = (toast, message, options = {}) => {
  const messageElement = toast.querySelector('[data-wishlist-toast-message]');
  const viewLink = toast.querySelector('[data-wishlist-toast-view]');
  if (messageElement) messageElement.textContent = message;
  if (viewLink) viewLink.hidden = !options.showView;
};

const scheduleWishlistToastHide = (toast) => {
  window.clearTimeout(toastTimeout);

  toastTimeout = window.setTimeout(() => {
    toast.classList.add('is-hiding');
    toast.classList.remove('is-visible');
  }, 3000);
};

const showWishlistToastNow = (toast, message, options = {}) => {
  setWishlistToastContent(toast, message, options);
  toast.classList.remove('is-visible');
  toast.classList.remove('is-hiding');

  window.requestAnimationFrame(() => {
    toast.classList.add('is-visible');
  });

  scheduleWishlistToastHide(toast);
};

const showWishlistToast = (message, options = {}) => {
  const toast = ensureWishlistToast();

  window.clearTimeout(toastTimeout);
  window.clearTimeout(toastTransitionTimeout);

  if (!toast.classList.contains('is-visible')) {
    showWishlistToastNow(toast, message, options);
    return;
  }

  queuedToast = { message, options };
  toast.style.transitionDuration = '0.18s';
  toast.classList.add('is-hiding');
  toast.classList.remove('is-visible');

  toastTransitionTimeout = window.setTimeout(() => {
    const nextToast = queuedToast;
    queuedToast = null;
    toast.style.transitionDuration = '';
    toast.classList.add('is-hiding');
    showWishlistToastNow(toast, nextToast.message, nextToast.options);
  }, 220);
};

const formatMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'CAD';

  return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).format((Number(cents) || 0) / 100);
};

const escapeHtml = (value) =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const parseNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getCartQuantityForVariant = (cart, variantId) => {
  const item = cart?.items?.find((cartItem) => String(cartItem.variant_id) === String(variantId));
  return item ? parseNumber(item.quantity, 0) : 0;
};

const fetchCart = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(WISHLIST_ADD_ERROR);
  return response.json();
};

const fetchWishlistVariantDetails = async (handle, variantId) => {
  const cacheKey = `${handle}:${variantId}`;
  if (variantDetailsCache.has(cacheKey)) return variantDetailsCache.get(cacheKey);

  const detailsPromise = (async () => {
    try {
      const response = await fetch(
        `/products/${encodeURIComponent(handle)}?view=wishlist-inventory&variant=${encodeURIComponent(variantId)}`
      );

      if (response.ok) {
        const text = await response.text();

        try {
          const product = JSON.parse(text);
          const variant = product?.variants?.find((item) => String(item.id) === String(variantId));
          if (variant) return variant;
        } catch {
          // Fall through to the product-page parser below.
        }
      }

      const fallbackResponse = await fetch(`/products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(variantId)}`);
      if (!fallbackResponse.ok) return null;

      const text = await fallbackResponse.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const scripts = html.querySelectorAll(
        'variant-picker script[type="application/json"], swatches-variant-picker-component script[type="application/json"]'
      );

      for (const script of scripts) {
        try {
          const variant = JSON.parse(script.textContent || '');
          if (String(variant?.id) === String(variantId)) return variant;
        } catch {
          // Try the next script.
        }
      }

      return null;
    } catch {
      return null;
    }
  })();

  variantDetailsCache.set(cacheKey, detailsPromise);

  const details = await detailsPromise;
  if (details) variantDetailsCache.set(cacheKey, details);

  return details;
};

const dispatchCartUpdate = (cart) => {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: {
          itemCount: cart.item_count,
        },
      },
    })
  );
};

const getWishlistAddLimit = (button, cartQuantity, variant = null) => {
  const ruleMaxValue = variant?.quantity_rule?.max ?? button.dataset.quantityRuleMax;
  const ruleMax = ruleMaxValue ? parseNumber(ruleMaxValue, WISHLIST_MAX_QUANTITY) : null;
  const selectorMax = ruleMax !== null ? Math.min(WISHLIST_MAX_QUANTITY, ruleMax) : WISHLIST_MAX_QUANTITY;
  const inventoryQuantity = parseNumber(variant?.inventory_quantity ?? button.dataset.inventoryQuantity, 0);
  const inventoryManagement = variant?.inventory_management ?? button.dataset.inventoryManagement ?? '';
  const inventoryPolicy = variant?.inventory_policy ?? button.dataset.inventoryPolicy ?? '';
  let stockRemaining = Infinity;

  if (inventoryManagement && inventoryQuantity > 0) {
    stockRemaining = Math.max(0, inventoryQuantity - cartQuantity);
  } else if (inventoryManagement && inventoryPolicy === 'deny') {
    stockRemaining = 0;
  }

  const purchaseRemaining = Math.max(0, selectorMax - cartQuantity);

  return Math.max(0, Math.min(purchaseRemaining, stockRemaining));
};

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.getAttribute('handle') || '';
    this.productTitle = this.getAttribute('product-title') || 'product';

    if (!this.button || !this.handle) return;

    this.button.addEventListener('click', this.onClick);
    window.addEventListener(CHANGE_EVENT, this.onWishlistChange);
    this.render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    window.removeEventListener(CHANGE_EVENT, this.onWishlistChange);
  }

  onClick = () => {
    const saved = hasProduct(this.handle);
    toggleProduct(this.handle);
    showWishlistToast(saved ? 'Item removed from wishlist' : 'Item added to wishlist', { showView: !saved });
  };

  onWishlistChange = () => {
    this.render();
  };

  render() {
    const saved = hasProduct(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.button.setAttribute('aria-label', `${saved ? 'Remove' : 'Add'} ${this.productTitle} ${saved ? 'from' : 'to'} wishlist`);
  }
}

class WishlistHeaderLink extends HTMLElement {
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.status = this.querySelector('[data-wishlist-status]');
    this.subtitle = this.querySelector('[data-wishlist-subtitle]');
    this.renderId = 0;
    window.addEventListener(CHANGE_EVENT, this.render);
    this.render();
  }

  disconnectedCallback() {
    this.renderId = (this.renderId || 0) + 1;
    window.removeEventListener(CHANGE_EVENT, this.render);
  }

  render = async () => {
    const renderId = (this.renderId || 0) + 1;
    this.renderId = renderId;
    const handles = getWishlist();

    if (!this.grid || !this.empty || !this.status) return;

    this.grid.innerHTML = '';
    this.empty.hidden = handles.length !== 0;
    this.status.hidden = false;
    this.status.textContent = this.getCountText(handles.length);
    this.renderSubtitle(handles.length);

    if (handles.length === 0) {
      return;
    }

    this.grid.innerHTML = Array.from({ length: handles.length }, () => this.renderSkeleton()).join('');

    const products = await Promise.all(handles.map((handle) => this.fetchProduct(handle)));

    if (renderId !== this.renderId) return;

    const validProducts = products.filter(Boolean);

    this.empty.hidden = validProducts.length !== 0;
    this.grid.innerHTML = validProducts.map((product) => this.renderProduct(product)).join('');
    this.status.textContent = this.getCountText(validProducts.length);
    this.renderSubtitle(validProducts.length);

    this.grid.querySelectorAll('[data-wishlist-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        removeProduct(button.getAttribute('data-wishlist-remove') || '');
        showWishlistToast('Item removed from wishlist');
      });
    });

    this.grid.querySelectorAll('[data-wishlist-add-to-cart]').forEach((button) => {
      button.addEventListener('click', this.onAddToCart);
    });

    this.grid.querySelectorAll('[data-wishlist-image]').forEach((image) => {
      const markLoaded = () => {
        image.classList.add('is-loaded');
        image.closest('.wishlist-card__image-shell')?.classList.add('is-loaded');
      };

      if (image instanceof HTMLImageElement && image.complete) {
        markLoaded();
      } else {
        image.addEventListener('load', markLoaded, { once: true });
      }
    });
  };

  onAddToCart = async (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const variantId = button.getAttribute('data-wishlist-add-to-cart');
    const productHandle = button.getAttribute('data-product-handle') || '';
    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const cart = await fetchCart();
      const cartQuantity = getCartQuantityForVariant(cart, variantId);
      const variantDetails = productHandle ? await fetchWishlistVariantDetails(productHandle, variantId) : null;
      const remainingAddable = getWishlistAddLimit(button, cartQuantity, variantDetails);

      if (remainingAddable < 1) {
        button.textContent = 'Max in cart';
        showWishlistToast(WISHLIST_ALREADY_IN_CART_ERROR);

        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = 'Add to cart';
        }, 1600);

        return;
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      if (!response.ok) {
        const errorCart = await fetchCart().catch(() => null);
        if (errorCart) dispatchCartUpdate(errorCart);
        throw new Error(WISHLIST_ADD_ERROR);
      }

      const updatedCart = await fetchCart();

      button.textContent = 'Added';
      dispatchCartUpdate(updatedCart);

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = 'Add to cart';
      }, 1600);
    } catch {
      button.disabled = false;
      button.textContent = 'Try again';

      window.setTimeout(() => {
        button.textContent = 'Add to cart';
      }, 1600);
    }
  };

  getCountText(count) {
    return `(${count} ${count === 1 ? 'item' : 'items'})`;
  }

  renderSubtitle(count) {
    if (!this.subtitle) return;

    this.subtitle.textContent =
      count === 0
        ? this.subtitle.getAttribute('data-empty-text') || ''
        : this.subtitle.getAttribute('data-saved-text') || '';
  }

  renderSkeleton() {
    return `
      <article class="wishlist-card product-card wishlist-card--skeleton" aria-hidden="true">
        <div class="product-card__content wishlist-card__content">
          <div class="product-card__image card-gallery wishlist-card__image-shell">
            <div class="wishlist-card__image-skeleton"></div>
          </div>
          <div class="wishlist-card__meta">
            <div class="wishlist-card__skeleton-line wishlist-card__skeleton-line--price"></div>
            <div class="wishlist-card__skeleton-line wishlist-card__skeleton-line--title"></div>
            <div class="wishlist-card__skeleton-line wishlist-card__skeleton-line--title-short"></div>
          </div>
        </div>
      </article>
    `;
  }

  async fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const productPromise = fetch(`/products/${encodeURIComponent(handle)}.js`).then((response) => {
        if (!response.ok) return null;
        return response.json();
      });

      productCache.set(handle, productPromise);
      const product = await productPromise;

      if (!product) {
        productCache.delete(handle);
        return null;
      }

      productCache.set(handle, product);
      return product;
    } catch {
      productCache.delete(handle);
      return null;
    }
  }

  renderProduct(product) {
    const image = product.featured_image || product.images?.[0] || '';
    const hoverImage = product.images?.find((item) => item !== image) || '';
    const url = product.url || `/products/${product.handle}`;
    const price = product.price_varies
      ? `From ${formatMoney(product.price_min || product.price)}`
      : formatMoney(product.price);
    const comparePrice =
      product.compare_at_price_max && product.compare_at_price_max > product.price
        ? `<s class="wishlist-card__compare-price">${escapeHtml(formatMoney(product.compare_at_price_max))}</s>`
        : '';
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const canAddToCart = Boolean(product.available && variant?.id);
    const quantityRule = variant?.quantity_rule || {};
    const soldOutBadge = !product.available
      ? '<div class="product-badges product-badges--sold-out wishlist-card__sold-out-badge"><div class="product-badges__badge product-badges__badge--rectangle product-badges__badge--sold-out">SOLD OUT</div></div>'
      : '';

    return `
      <article class="wishlist-card product-card">
        <a class="product-card__link wishlist-card__link" href="${escapeHtml(url)}" aria-label="${escapeHtml(product.title)}"></a>
        <div class="product-card__content wishlist-card__content">
          <div class="product-card__image card-gallery wishlist-card__image-shell">
            <div class="wishlist-card__image-skeleton"></div>
            ${
              image
                ? `<img class="wishlist-card__image" src="${escapeHtml(this.getSizedImage(image))}" alt="${escapeHtml(product.title)}" loading="lazy" draggable="false" data-wishlist-image>`
                : ''
            }
            ${
              hoverImage
                ? `<img class="wishlist-card__image wishlist-card__image--hover" src="${escapeHtml(this.getSizedImage(hoverImage))}" alt="${escapeHtml(product.title)}" loading="lazy" draggable="false">`
                : ''
            }
            ${soldOutBadge}
            ${
              canAddToCart
                ? `<button
                    type="button"
                    class="wishlist-card__add-to-cart"
                    data-wishlist-add-to-cart="${escapeHtml(variant.id)}"
                    data-product-handle="${escapeHtml(product.handle || '')}"
                    data-inventory-quantity="${escapeHtml(variant.inventory_quantity ?? 0)}"
                    data-inventory-management="${escapeHtml(variant.inventory_management || '')}"
                    data-inventory-policy="${escapeHtml(variant.inventory_policy || '')}"
                    data-quantity-rule-max="${quantityRule.max ? escapeHtml(quantityRule.max) : ''}"
                  >Add to cart</button>`
                : ''
            }
            <button type="button" class="wishlist-card__remove button-unstyled" data-wishlist-remove="${escapeHtml(product.handle)}" aria-label="Remove ${escapeHtml(product.title)} from wishlist" aria-pressed="true">
              ${WISHLIST_ICON}
            </button>
          </div>
          <div class="wishlist-card__meta">
            <div class="wishlist-card__price">${escapeHtml(price)}${comparePrice}</div>
            <div class="wishlist-card__title">${escapeHtml(product.title)}</div>
          </div>
        </div>
      </article>
    `;
  }

  getSizedImage(src) {
    if (!src) return '';

    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=700`;
  }
}

if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
if (!customElements.get('wishlist-header-link')) customElements.define('wishlist-header-link', WishlistHeaderLink);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
